<template>
    <div class="tw:p-6">
        <div class="tw:mb-6">
            <h3 class="tw:text-lg tw:font-semibold tw:text-gray-900 tw:flex tw:items-center tw:gap-2">
                Datos personales
            </h3>
            <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:mt-1">Información personal, responsables y documentación</p>
        </div>

        <!-- Bloque 1: Responsables + Contacto -->
        <div class="tw:space-y-3">
            <div class="tw:space-y-2">
                <h4 class="tw:text-sm tw:font-semibold tw:text-gray-600 tw:flex tw:items-center tw:gap-1.5">
                    <i class="fas fa-user-tie tw:text-gray-400"></i> Responsable 1
                </h4>
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-3">
                    <DataField icon="fas fa-user" label="Nombre" :value="base.nombre_responsable" />
                    <DataField icon="fas fa-user" label="Apellidos" :value="base.apellidos_responsable" />
                    <DataField icon="fas fa-id-card" label="DNI" :value="base.dni_responsable" />
                    <DataField icon="fas fa-envelope" label="Email" :value="base.email_responsable" />
                    <DataField icon="fas fa-phone-alt" label="Teléfono" :value="base.telefono_responsable" />
                    <!-- Campos personalizados del responsable 1 -->
                    <DataField
                        v-for="field in base.fields_responsable_1"
                        :key="field.name"
                        icon="fas fa-tag"
                        :label="humanizeFieldName(field.name)"
                        :value="field.value"
                    />
                </div>
            </div>

            <div v-if="base.nombre_responsable_2" class="tw:pt-4 tw:border-t tw:border-gray-100 tw:space-y-2">
                <h4 class="tw:text-sm tw:font-semibold tw:text-gray-600 tw:flex tw:items-center tw:gap-1.5">
                    <i class="fas fa-user-tie tw:text-gray-400"></i> Responsable 2
                </h4>
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-3">
                    <DataField icon="fas fa-user" label="Nombre" :value="base.nombre_responsable_2" />
                    <DataField icon="fas fa-user" label="Apellidos" :value="base.apellidos_responsable_2" />
                    <DataField icon="fas fa-id-card" label="DNI" :value="base.dni_responsable_2" />
                    <DataField icon="fas fa-envelope" label="Email" :value="base.email_responsable_2" />
                    <DataField icon="fas fa-phone-alt" label="Teléfono" :value="base.telefono_responsable_2" />
                    <!-- Campos personalizados del responsable 2 -->
                    <DataField
                        v-for="field in base.fields_responsable_2"
                        :key="field.name"
                        icon="fas fa-tag"
                        :label="humanizeFieldName(field.name)"
                        :value="field.value"
                    />
                </div>
            </div>

            <div class="tw:bg-gray-100 tw:h-[0.5px] tw:w-full"></div>

            <div>
                <h4 class="tw:text-sm tw:font-semibold tw:text-gray-600 tw:flex tw:items-center tw:gap-1.5 tw:mb-4">
                    <i class="fas fa-address-card tw:text-gray-400"></i> Contacto y dirección
                </h4>
                <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-3 tw:gap-4">
                    <DataField icon="fas fa-home" label="Dirección" :value="base.direccion" />
                    <DataField icon="fas fa-phone-alt" label="Teléfono" :value="base.telefono" />
                    <DataField icon="fas fa-globe-americas" label="País" :value="base.pais_de_nacimiento" />
                </div>
            </div>
        </div>

        <div class="tw:bg-gray-100 tw:h-[0.5px] tw:w-full tw:my-6"></div>

        <!-- Bloque 2: Documentación -->
        <div>
            <h4 class="tw:text-sm tw:font-semibold tw:text-gray-600 tw:flex tw:items-center tw:gap-1.5 tw:mb-4">
                <i class="fas fa-file-alt tw:text-gray-400"></i> Documentación
            </h4>

            <!-- Dropzone reutilizable -->
            <Dropzone
                :url="url"
                :accepted-files="acceptFormats"
                :documents="base.documentos"
                :uploading="uploading"
                :uploading-name="uploadingName"
                :upload-error="uploadError"
                @file-selected="onFileSelected"
                @edit-document="openEditDocumento"
                @delete-document="openBorrarDocumento"
                @download-document="onDownloadDocument"
                @view-document="onViewDocument"
            />

            <!-- Modal: Vista previa -->
            <ViewDocumento
                v-if="viewingDoc"
                :documento="viewingDoc"
                :loading="downloadingDocumento"
                @close="closeViewDocument"
                @download="onDownloadDocument"
            />

            <!-- Modal: Editar documento -->
            <EditDocumento
                v-if="editingDocumento"
                :documento="editingDocumento"
                @close="editingDocumento = null"
                @confirm="onEditConfirm"
            />

            <!-- Modal: Confirmar borrar -->
            <BorrarModalComponent
                :name="'borrar-documento-' + (borrandoDocumento?.id || '')"
                title="Borrar documento"
                :borrar-title="'¿Borrar «' + (borrandoDocumento?.nombre || '') + '»?'"
                confirm-button="Esta acción no se puede deshacer"
                @close="onDeleteClose"
                @confirm="onDeleteConfirm"
            />
        </div>

        <div class="tw:bg-gray-100 tw:h-[0.5px] tw:w-full tw:my-6"></div>

        <!-- Bloque 3: Observaciones -->
        <div>
            <h4 class="tw:text-sm tw:font-semibold tw:text-gray-600 tw:flex tw:items-center tw:gap-1.5 tw:mb-4">
                <i class="fas fa-comment-alt tw:text-gray-400"></i> Observaciones
            </h4>
            <div class="tw:relative tw:w-full">
                <BaseTextarea
                    :value="observacion"
                    name="observaciones"
                    label="Añadir observación"
                    rows="3"
                    @input="observacion = $event"
                />
            </div>
            <div class="tw:flex tw:justify-end tw:mt-3">
                <Button @click="guardarObservacion" size="sm">
                    Guardar observación
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, nextTick } from 'vue';
    import { getCurrentInstance } from 'vue';
    import store from '@/store';
    import { config } from '@/config.js';
    import DataField from '@/modules/admin/ui/DataField.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import BaseTextarea from '@/modules/admin/ui/BaseTextarea.vue';
    import Dropzone from '@/modules/admin/ui/Dropzone.vue';
    import EditDocumento from '@/components/backend/components/documentos/modals/EditDocumento.vue';
    import BorrarModalComponent from '@/components/backend/components/BorrarModalComponent.vue';
    import ViewDocumento from '@/modules/admin/documentos/components/ViewDocumento.vue';
    import { useToast } from '@/composables/common/useToast';
    import { useBaseDocumentDownload } from '@/composables/admin/bases/useBaseDocumentDownload';
    import { humanizeFieldName } from '@/utils/format';

    const props = defineProps({
        base: {
            type: Object,
            required: true
        },
        url: {
            type: String,
            default: null
        },
        listaId: {
            type: [String, Number],
            required: true
        },
        baseId: {
            type: [String, Number],
            required: true
        }
    });

    const emit = defineEmits(['refresh', 'editar-observacion']);

    const instance = getCurrentInstance();
    const toast = useToast();

    // ── Download document ──────────────────────────────────
    //
    // El composable específico de bases encapsula el endpoint y la
    // construcción de la petición; la vista solo dispara la descarga.
    // Pasamos getters (no primitivos) para que el composable lea los IDs
    // en cada descarga — si la vista cambia de props sin remontar, los
    // identificadores se mantienen frescos.
    const { download: downloadDocumento, loading: downloadingDocumento } = useBaseDocumentDownload(
        () => props.listaId,
        () => props.baseId
    );

    function onDownloadDocument(doc) {
        if (!doc) return;
        downloadDocumento(doc);
    }

    // ── View document ──────────────────────────────────────

    const viewingDoc = ref(null);

    function onViewDocument(doc) {
        viewingDoc.value = doc;
        // El modal usa vue-js-modal: necesita $modal.show() para hacerse
        // visible. nextTick asegura que el <ViewDocumento> esté montado
        // antes del show (patrón de DocumentosCard.vue).
        nextTick(() => {
            instance.proxy.$modal.show(`view-documento-${doc.id}`);
        });
    }

    function closeViewDocument() {
        viewingDoc.value = null;
    }

    // ── Observaciones ──────────────────────────────────────

    const observacion = ref(props.base?.observaciones_club || '');

    const guardarObservacion = () => {
        emit('editar-observacion', observacion.value);
    };

    // ── Document upload ────────────────────────────────────

    const acceptFormats = 'image/jpeg,image/png,image/gif,image/bmp,image/tiff,application/pdf,.pdf';
    const uploading = ref(false);
    const uploadingName = ref('');
    const uploadError = ref('');

    function onFileSelected(file) {
        uploadError.value = '';
        uploadingName.value = file.name;

        // Validate type
        const allowed = acceptFormats.split(',').map(s => s.trim());
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        const mimeOk = allowed.includes(file.type);
        const extOk = allowed.includes(ext);
        if (!mimeOk && !extOk) {
            uploadError.value = 'Formato no soportado. Usa: PDF, JPG, PNG, GIF, BMP, TIFF';
            return;
        }

        // Validate size (10 MB)
        if (file.size > 10 * 1024 * 1024) {
            uploadError.value = 'El archivo es demasiado grande. Máximo 10 MB.';
            return;
        }

        uploading.value = true;

        const formData = new FormData();
        formData.append('file', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', props.url);
        xhr.setRequestHeader('Accept', 'application/json');

        const hashTeatro = localStorage.getItem('hash_teatro');
        if (hashTeatro) xhr.setRequestHeader('Hash-Teatro', hashTeatro);

        xhr.onload = () => {
            uploading.value = false;
            if (xhr.status >= 200 && xhr.status < 300) {
                toast.success('Documento subido correctamente');
                emit('refresh');
            } else {
                uploadError.value = 'Error del servidor al subir el documento. Inténtalo de nuevo.';
            }
        };

        xhr.onerror = () => {
            uploading.value = false;
            uploadError.value = 'Error de conexión al subir el documento.';
        };

        xhr.ontimeout = () => {
            uploading.value = false;
            uploadError.value = 'La subida tardó demasiado. Inténtalo de nuevo.';
        };

        xhr.send(formData);
    }

    // ── Edit document ──────────────────────────────────────

    const editingDocumento = ref(null);

    async function openEditDocumento(doc) {
        editingDocumento.value = doc;
        await nextTick();
        instance.proxy.$modal.show('editar-' + doc.id);
    }

    async function onEditConfirm(updatedDoc) {
        try {
            await store.dispatch(
                config.getVuexAlias('back.bases.editar-documento'),
                {
                    lista_id: props.listaId,
                    base_id: props.baseId,
                    documento_id: updatedDoc.id,
                    documento: updatedDoc
                }
            );
            toast.success('Documento actualizado');
            instance.proxy.$modal.hide('editar-' + updatedDoc.id);
            editingDocumento.value = null;
            emit('refresh');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al actualizar el documento');
        }
    }

    // ── Delete document ────────────────────────────────────

    const borrandoDocumento = ref(null);

    async function openBorrarDocumento(doc) {
        borrandoDocumento.value = doc;
        await nextTick();
        instance.proxy.$modal.show('borrar-documento-' + doc.id);
    }

    function onDeleteClose() {
        if (borrandoDocumento.value) {
            instance.proxy.$modal.hide('borrar-documento-' + borrandoDocumento.value.id);
        }
        borrandoDocumento.value = null;
    }

    async function onDeleteConfirm() {
        const doc = borrandoDocumento.value;
        if (!doc) return;

        try {
            await store.dispatch(
                config.getVuexAlias('back.bases.borrar-documento'),
                {
                    lista_id: props.listaId,
                    base_id: props.baseId,
                    documento_id: doc.id
                }
            );
            toast.success('Documento borrado');
            instance.proxy.$modal.hide('borrar-documento-' + doc.id);
            borrandoDocumento.value = null;
            emit('refresh');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Error al borrar el documento');
        }
    }
</script>
